import { CATEGORY_META, categoryKey, groupServices, prettyCountry, prettyLabel } from "./catalog";

const STOP = new Set(
  "the a an and or of to for in on at with from our your you we is are was were do does did can could what which who how when where why tell me about please this that these those into over any some just like want need".split(" ")
);

function plain(html) {
  return String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function words(text) {
  return plain(text)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 1 && !STOP.has(word));
}

function overlap(questionWords, text) {
  const hay = new Set(words(text));
  return questionWords.filter((word) => hay.has(word)).length;
}

function clip(text, max = 320) {
  const clean = plain(text);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).replace(/\s+\S*$/, "")}…`;
}

function headline(item) {
  const blob = `${item?.title || ""} ${item?.location || ""}`;
  if (/ma campagne|muhazi/i.test(blob)) return "Rwanda: 25kWp Solar Power System Installation at Ma Campagne Resort Muhazi";
  if (/fada/i.test(blob)) return "Tchad: 100kWp Solar power system Installation at Fada Hospital";
  if (/gigiri|gigili|un office/i.test(blob)) return "Kenya: 515kWp Solar power system for UN office Gigiri, Nairobi";
  if (/kirehe|pumping/i.test(blob)) return "Rwanda: Solar water pumping system of 75kWp at Kirehe District";
  return item?.title || "Project";
}

function companyOf(site) {
  const company = site?.settings?.company || {};
  return {
    name: company.name || "Renerg (R) Ltd",
    phone: company.phone || "+250 791 502 063",
    email: company.email || "renergrwanda@gmail.com",
    address: company.address || "Nyarugenge, Kigali, Rwanda",
  };
}

function aboutText(site) {
  const block = (site?.content || []).find((item) => item.section === "about" && plain(item.body));
  return plain(block?.body) || "RENERG (R) LTD is a registered private company in Rwanda. It delivers solar energy, green buildings, and e-mobility, with more than 15 years of work and projects across African countries.";
}

function listJoin(items) {
  if (items.length <= 1) return items[0] || "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function answerSiteQuestion(site, rawQuestion) {
  const question = String(rawQuestion || "").trim();
  const company = companyOf(site);
  const qWords = words(question);
  const lower = question.toLowerCase();
  const services = site?.services || [];
  const projects = site?.projects || [];
  const contact = {
    reply: `You can reach ${company.name} at ${company.phone} or ${company.email}. The office is in ${company.address}. Hours are Monday to Friday, 8:00 to 17:00, and Saturday, 9:00 to 13:00.`,
    links: [{ label: "Contact page", to: "/contact" }],
  };

  if (!question) {
    return { reply: "Ask about a service, a project, or how to contact RENERG.", links: [] };
  }

  if (/^(hi|hello|hey|good morning|good afternoon)\b/i.test(lower) && qWords.length < 4) {
    return {
      reply: `Hello. I answer from the information published on this website. I can tell you about ${company.name}'s services, projects, and how to get in touch.`,
      links: [
        { label: "Services", to: "/services" },
        { label: "Projects", to: "/projects" },
      ],
    };
  }

  if (/\b(price|prices|cost|costs|quote|quotation|how much)\b/.test(lower)) {
    return {
      reply: "Prices are not listed on the website. RENERG sends a written offer after the site and the load are known. Use the contact form, or call the team, and they will reply with the next step.",
      links: [{ label: "Request a quote", to: "/contact" }],
    };
  }

  if (/\b(hour|hours|open|opening|close|closing)\b/.test(lower)) {
    return {
      reply: "The office is open Monday to Friday, 8:00 to 17:00, and Saturday, 9:00 to 13:00.",
      links: [{ label: "Contact page", to: "/contact" }],
    };
  }

  if (/\b(phone|call|email|e-mail|mail|address|office|where|location|contact|reach|visit)\b/.test(lower)) {
    return contact;
  }

  if (/\b(who|about|company|mission|vision|renerg)\b/.test(lower) && !/\b(project|service)\b/.test(lower)) {
    return {
      reply: clip(aboutText(site), 420),
      links: [{ label: "About RENERG", to: "/about" }],
    };
  }

  const generic = new Set(["project", "projects", "solar", "power", "system", "installation", "published", "work", "plant", "plants", "energy", "service", "services"]);
  const projectHits = projects
    .map((item) => {
      const title = headline(item);
      const place = [prettyCountry(item.country), item.location, item.capacity].filter(Boolean).join(" ");
      const blob = `${title} ${place} ${item.description || ""}`;
      const score = overlap(qWords, blob);
      const hay = new Set(words(blob));
      const named = qWords.filter((word) => hay.has(word) && !generic.has(word));
      return { item, title, place, score, named };
    })
    .filter((hit) => hit.named.length > 0 || hit.score > 0)
    .sort((a, b) => b.named.length - a.named.length || b.score - a.score);

  const countryNames = [...new Set(["Rwanda", "Kenya", "Tchad", ...projects.map((item) => prettyCountry(item.country))])];
  const namedCountry = countryNames.find((name) => lower.includes(name.toLowerCase()) || (name === "Tchad" && /\b(chad|tchad)\b/.test(lower)));

  if (namedCountry && /\b(project|projects|work|plant|plants|installation)\b/.test(lower)) {
    const list = projects.filter((item) => prettyCountry(item.country) === namedCountry);
    const lines = list.map((item) => headline(item));
    return {
      reply: list.length
        ? `Published work in ${namedCountry}: ${listJoin(lines)}.`
        : `There is no published project for ${namedCountry} yet.`,
      links: [{ label: `Projects in ${namedCountry}`, to: `/projects/country/${encodeURIComponent(namedCountry)}` }],
    };
  }

  if (projectHits[0] && projectHits[0].named.length > 0) {
    const hit = projectHits[0];
    const facts = [prettyCountry(hit.item.country), hit.item.location, hit.item.capacity].filter(Boolean).join(" · ");
    const detail = clip(hit.item.description, 280);
    return {
      reply: detail
        ? `${hit.title}. ${facts}. ${detail}`
        : `${hit.title}. ${facts}. Open the project page for the photographs and the full note.`,
      links: [{ label: "Open this project", to: `/projects/${hit.item.id}` }],
    };
  }

  if (/\b(project|projects|portfolio|plant|plants|installation)\b/.test(lower)) {
    if (!projects.length) {
      return { reply: "No projects are published on the site yet.", links: [{ label: "Projects", to: "/projects" }] };
    }
    const lines = projects.slice(0, 4).map((item) => headline(item));
    return {
      reply: `Published projects include ${listJoin(lines)}. You can filter them by country on the projects page.`,
      links: [{ label: "All projects", to: "/projects" }],
    };
  }

  const grouped = groupServices(services);
  const serviceHits = services
    .map((item) => ({
      item,
      score: overlap(qWords, `${item.title || ""} ${item.description || ""} ${prettyLabel(item.category)}`),
    }))
    .filter((hit) => hit.score > 0)
    .sort((a, b) => b.score - a.score);

  const oneService = serviceHits[0] && (serviceHits[0].score >= 2 || (serviceHits[0].score >= 1 && !/\b(services|service)\b/.test(lower)));
  if (oneService) {
    const item = serviceHits[0].item;
    const cat = CATEGORY_META[categoryKey(item.category)]?.title || prettyLabel(item.category);
    const detail = clip(item.description, 280);
    return {
      reply: detail
        ? `${item.title} is part of ${cat}. ${detail}`
        : `${item.title} is part of ${cat}. Open the service page for what the work includes.`,
      links: [{ label: item.title, to: `/services/${item.id}` }],
    };
  }

  const categoryHit = Object.values(grouped).find((cat) => {
    const blob = `${cat.title} ${cat.key} ${cat.description}`;
    return overlap(qWords, blob) > 0 || lower.includes(String(cat.title).toLowerCase());
  });

  if (categoryHit && /\b(solar|photovoltaic|building|buildings|real estate|mobility|charging|charger|ev)\b/.test(lower)) {
    const names = categoryHit.services.map((item) => item.title).filter(Boolean);
    return {
      reply: `${categoryHit.title}: ${categoryHit.description} The published services are ${listJoin(names)}.`,
      links: [{ label: categoryHit.title, to: `/services/category/${categoryHit.key}` }],
    };
  }

  if (/\b(service|services|offer|do you do|what do you)\b/.test(lower)) {
    if (!services.length) {
      return {
        reply: "RENERG publishes work in solar energy, green buildings, and e-mobility. The service list is still loading or is not published yet.",
        links: [{ label: "Services", to: "/services" }],
      };
    }
    const groups = Object.values(grouped).map((cat) => {
      const names = cat.services.map((item) => item.title).filter(Boolean);
      return `${cat.title} (${listJoin(names)})`;
    });
    return {
      reply: `RENERG's published services are ${listJoin(groups)}.`,
      links: [{ label: "All services", to: "/services" }],
    };
  }

  const blocks = (site?.content || [])
    .map((block) => ({
      block,
      score: overlap(qWords, `${block.title || ""} ${plain(block.body)}`),
    }))
    .filter((hit) => hit.score >= 2)
    .sort((a, b) => b.score - a.score);

  if (blocks[0]) {
    const block = blocks[0].block;
    const to = block.section === "about" || block.section === "values" ? "/about" : "/contact";
    return {
      reply: clip(`${block.title ? `${block.title}. ` : ""}${plain(block.body)}`, 420),
      links: [{ label: "Read more", to }],
    };
  }

  if (projectHits[0]) {
    const hit = projectHits[0];
    return {
      reply: `The closest published project is ${hit.title}.`,
      links: [{ label: "Open this project", to: `/projects/${hit.item.id}` }],
    };
  }

  return {
    reply: "I can answer from what is published on this site: solar, green buildings, e-mobility, the project list, and how to contact the team. I do not have an answer for that question in the site information.",
    links: [
      { label: "Services", to: "/services" },
      { label: "Projects", to: "/projects" },
      { label: "Contact", to: "/contact" },
    ],
  };
}
